function formatPrice(value) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function init() {
  const root = document.querySelector("[data-configurator]");
  if (!root) {
    return;
  }

  const modelKey = root.dataset.model;
  // v2 — bumped after the spec-led redesign drained option image refs and
  // changed the persisted shape; old entries reference option ids that no
  // longer match the current TOML.
  const storageKey = `vanyx-cfg-v2-${modelKey}`;

  // Format all static price displays. Every shown price is a starting figure
  // (ex works, before config-dependent variation), so prefix it with a small
  // muted "ab"/"from" qualifier.
  const fromWord = document.documentElement.lang === "en" ? "from" : "ab";
  root.querySelectorAll("[data-price-display]").forEach((el) => {
    el.innerHTML =
      `<span class="wc-configurator__price-from">${fromWord}</span>` +
      formatPrice(Number(el.dataset.priceDisplay));
  });

  // Load option details from embedded JSON
  const detailsScript = root.querySelector("[data-option-details]");
  let optionDetails = {};
  if (detailsScript) {
    try {
      optionDetails = JSON.parse(detailsScript.textContent);
    } catch {
      // Silent fail
    }
  }

  const summaryVariant = root.querySelector(
    "[data-configurator-summary-variant]",
  );
  const summaryOptions = root.querySelector(
    "[data-configurator-summary-options]",
  );
  const totalDisplay = root.querySelector("[data-configurator-total]");
  const mobileTotalDisplay = root.querySelector(
    "[data-configurator-mobile-total]",
  );

  // Form fields
  const fieldVariant = root.querySelector("[data-configurator-field-variant]");
  const fieldOptions = root.querySelector("[data-configurator-field-options]");
  const fieldTotal = root.querySelector("[data-configurator-field-total]");

  // Info popup
  const popup = root.querySelector("[data-info-popup]");
  const popupTitle = popup
    ? popup.querySelector("[data-info-popup-title]")
    : null;
  const popupDesc = popup
    ? popup.querySelector("[data-info-popup-desc]")
    : null;
  const popupPrice = popup
    ? popup.querySelector("[data-info-popup-price]")
    : null;
  const popupList = popup
    ? popup.querySelector("[data-info-popup-list]")
    : null;

  function getActiveVariant() {
    return root.querySelector("[data-configurator-variant]:checked");
  }

  function getSelectedOptions() {
    return Array.from(
      root.querySelectorAll("[data-configurator-option]:checked"),
    );
  }

  function getSelectedTiers() {
    return Array.from(
      root.querySelectorAll("[data-configurator-tier]:checked"),
    ).filter((r) => r.value !== "none");
  }

  // ── Modal a11y: focus-trigger memory + focus-into-dialog on open ──
  let lastFocusedTrigger = null;

  function openDialog(popupEl, trigger) {
    lastFocusedTrigger = trigger || document.activeElement;
    popupEl.hidden = false;
    document.body.style.overflow = "hidden";
    // Move focus to the close button so screen-reader users land inside
    // the dialog rather than at the bottom of the page behind it.
    const focusTarget =
      popupEl.querySelector("[data-info-popup-close], [data-confirm-cancel], [data-quote-close]");
    if (focusTarget) {
      // Defer one frame so [hidden] removal has flushed and the browser
      // accepts the focus call.
      requestAnimationFrame(() => focusTarget.focus());
    }
  }

  function closeDialog(popupEl) {
    popupEl.hidden = true;
    document.body.style.overflow = "";
    if (lastFocusedTrigger && typeof lastFocusedTrigger.focus === "function") {
      lastFocusedTrigger.focus();
    }
    lastFocusedTrigger = null;
  }

  // ── Confirm popup (used for unmet dependencies) ──
  const confirmPopup = root.querySelector("[data-confirm-popup]");
  const confirmTitle = confirmPopup
    ? confirmPopup.querySelector("[data-confirm-title]")
    : null;
  const confirmDesc = confirmPopup
    ? confirmPopup.querySelector("[data-confirm-desc]")
    : null;
  const confirmAcceptLabel = confirmPopup
    ? confirmPopup.querySelector("[data-confirm-accept-label]")
    : null;
  let pendingConfirm = null;

  function showConfirm(title, desc, acceptLabel, onAccept, onCancel) {
    if (!confirmPopup) {
      onAccept();
      return;
    }
    confirmTitle.textContent = title;
    confirmDesc.textContent = desc;
    if (confirmAcceptLabel) {
      confirmAcceptLabel.textContent = acceptLabel;
    }
    pendingConfirm = { onAccept, onCancel };
    openDialog(confirmPopup);
  }

  function acceptConfirm() {
    if (!pendingConfirm) {
      return;
    }
    const { onAccept } = pendingConfirm;
    pendingConfirm = null;
    if (confirmPopup) {
      closeDialog(confirmPopup);
    }
    onAccept();
  }

  function cancelConfirm() {
    if (!pendingConfirm) {
      return;
    }
    const { onCancel } = pendingConfirm;
    pendingConfirm = null;
    if (confirmPopup) {
      closeDialog(confirmPopup);
    }
    if (onCancel) {
      onCancel();
    }
  }

  function getUnmetDeps(label) {
    const selectedIds = new Set([
      ...getSelectedOptions().map((cb) => cb.value),
      ...getSelectedTiers().map((r) => r.value),
    ]);
    const missing = [];
    if (label.dataset.requires && !selectedIds.has(label.dataset.requires)) {
      missing.push(label.dataset.requires);
    }
    if (label.dataset.requiresAll) {
      label.dataset.requiresAll.split(",").forEach((d) => {
        if (!selectedIds.has(d)) {
          missing.push(d);
        }
      });
    }
    return missing;
  }

  // Returns true if a popup was shown (update should bail)
  function checkDependencies(changed) {
    const isRadio = changed.type === "radio";
    // For radios, "none" never has dependencies; for checkboxes, only check when checking
    if (isRadio && changed.value === "none") {
      return false;
    }
    if (!isRadio && !changed.checked) {
      return false;
    }
    const label = changed.closest("[data-option-id]");
    if (!label) {
      return false;
    }
    const missing = getUnmetDeps(label);
    if (!missing.length) {
      return false;
    }

    const isEn = document.documentElement.lang === "en";
    const missingDetails = missing.map((id) => {
      const d = optionDetails[id] || {};
      return { id, name: d.title || id, price: d.price || 0 };
    });
    const missingNames = missingDetails.map((d) => d.name).join(", ");
    const missingTotal = missingDetails.reduce((s, d) => s + d.price, 0);

    // Revert immediately so enforceDependencies doesn't fight us
    if (isRadio) {
      const defaultRadio = root.querySelector(
        `input[name="${changed.name}"][value="none"]`,
      );
      if (defaultRadio) {
        defaultRadio.checked = true;
      }
    } else {
      changed.checked = false;
    }

    showConfirm(
      isEn ? "Required options" : "Benötigte Optionen",
      isEn
        ? `This option requires: ${missingNames} (+${formatPrice(missingTotal)}). Add them?`
        : `Diese Option erfordert: ${missingNames} (+${formatPrice(missingTotal)}). Hinzufügen?`,
      isEn ? "Add all" : "Alle hinzufügen",
      () => {
        // Enable deps first, then the requested option
        missing.forEach((id) => {
          const cb = root.querySelector(
            `[data-option-id="${id}"] [data-configurator-option]`,
          );
          if (cb) {
            cb.checked = true;
          }
          // Also check tier radios
          const tierRadio = root.querySelector(
            `[data-option-id="${id}"] [data-configurator-tier]`,
          );
          if (tierRadio) {
            tierRadio.checked = true;
          }
        });
        changed.checked = true;
        update();
      },
      () => {
        // Cancel — already reverted
      },
    );
    return true;
  }

  function enforceDependencies() {
    // When an option is unchecked, uncheck any that depended on it
    const selectedIds = new Set([
      ...getSelectedOptions().map((cb) => cb.value),
      ...getSelectedTiers().map((r) => r.value),
    ]);

    // Enforce on checkbox options
    root.querySelectorAll("[data-option-id] [data-configurator-option]").forEach((cb) => {
      if (!cb.checked) {
        return;
      }
      const label = cb.closest("[data-option-id]");
      if (!label) {
        return;
      }
      let met = true;
      if (label.dataset.requires) {
        met = selectedIds.has(label.dataset.requires);
      }
      if (label.dataset.requiresAll) {
        met = label.dataset.requiresAll.split(",").every((d) => selectedIds.has(d));
      }
      if (!met) {
        cb.checked = false;
      }
    });

    // Enforce on tier radio options
    root.querySelectorAll("[data-option-id] [data-configurator-tier]").forEach((radio) => {
      if (!radio.checked || radio.value === "none") {
        return;
      }
      const label = radio.closest("[data-option-id]");
      if (!label) {
        return;
      }
      let met = true;
      if (label.dataset.requires) {
        met = selectedIds.has(label.dataset.requires);
      }
      if (label.dataset.requiresAll) {
        met = label.dataset.requiresAll.split(",").every((d) => selectedIds.has(d));
      }
      if (!met) {
        const defaultRadio = root.querySelector(
          `input[name="${radio.name}"][value="none"]`,
        );
        if (defaultRadio) {
          defaultRadio.checked = true;
        }
      }
    });
  }

  function updateCategoryCounts() {
    root.querySelectorAll("[data-category]").forEach((details) => {
      const catKey = details.dataset.category;
      const countEl = details.querySelector(
        `[data-category-count="${catKey}"]`,
      );
      if (!countEl) {
        return;
      }
      const checkedAddons = details.querySelectorAll(
        "[data-configurator-option]:checked",
      ).length;
      const selectedTiers = details.querySelectorAll(
        "[data-configurator-tier]:checked:not([value='none'])",
      ).length;
      const total = countEl.dataset.categoryTotal || 0;
      const count = checkedAddons + selectedTiers;
      countEl.textContent = `${count} / ${total}`;
      countEl.classList.toggle("is-active", count > 0);
    });
  }

  function saveState() {
    const variantInput = getActiveVariant();
    const tiers = {};
    root.querySelectorAll("[data-configurator-tier]:checked").forEach((r) => {
      tiers[r.dataset.tierGroup] = r.value;
    });
    const state = {
      variant: variantInput ? variantInput.value : "",
      options: getSelectedOptions().map((cb) => cb.value),
      tiers,
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Storage full or unavailable
    }
  }

  function restoreState() {
    let state;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return;
      }
      state = JSON.parse(raw);
    } catch {
      return;
    }

    // Restore variant
    if (state.variant) {
      const radio = root.querySelector(
        `[data-configurator-variant][value="${state.variant}"]`,
      );
      if (radio) {
        radio.checked = true;
      }
    }

    // Restore tier selections
    if (state.tiers) {
      Object.entries(state.tiers).forEach(([group, value]) => {
        const radio = root.querySelector(
          `[data-configurator-tier][data-tier-group="${group}"][value="${value}"]`,
        );
        if (radio) {
          radio.checked = true;
        }
      });
    }

    // Legacy: if saved options include tier option IDs, restore as tiers
    if (state.options && state.options.length) {
      // Multiple passes to handle chained dependencies
      for (let pass = 0; pass < 3; pass++) {
        state.options.forEach((id) => {
          const cb = root.querySelector(
            `[data-configurator-option][value="${id}"]`,
          );
          if (cb && !cb.disabled) {
            cb.checked = true;
          }
          // Handle legacy: option might now be a tier radio
          const tierRadio = root.querySelector(
            `[data-configurator-tier][value="${id}"]`,
          );
          if (tierRadio) {
            tierRadio.checked = true;
          }
        });
        enforceDependencies();
      }
    }
  }

  function update(changed) {
    if (changed) {
      if (checkDependencies(changed)) {
        return; // Popup shown — accept/cancel callback will call update()
      }
    }
    enforceDependencies();
    updateCategoryCounts();

    // Sync add-on card selected state
    root.querySelectorAll(".wc-configurator__option[data-option-id]").forEach((card) => {
      const cb = card.querySelector("[data-configurator-option]");
      card.classList.toggle("is-selected", cb && cb.checked);
    });

    // Sync tier card selected state
    root.querySelectorAll(".wc-configurator__tier-card").forEach((card) => {
      const radio = card.querySelector("[data-configurator-tier]");
      card.classList.toggle("is-selected", radio && radio.checked && radio.value !== "none");
    });

    const variantInput = getActiveVariant();
    const variantPrice = variantInput
      ? Number(variantInput.dataset.price)
      : 0;
    const selectedOpts = getSelectedOptions();
    const optionsTotal = selectedOpts.reduce(
      (sum, cb) => sum + Number(cb.dataset.price),
      0,
    );
    const selectedTiers = getSelectedTiers();
    const tierTotal = selectedTiers.reduce(
      (sum, r) => sum + Number(r.dataset.price),
      0,
    );
    const total = variantPrice + optionsTotal + tierTotal;

    // Spec list under the variant row: show the selected variant's, hide the rest.
    if (variantInput) {
      root.querySelectorAll("[data-variant-details]").forEach((el) => {
        el.hidden = el.dataset.variantDetails !== variantInput.value;
      });
    }

    // Summary variant
    if (summaryVariant && variantInput) {
      const varLabel = variantInput
        .closest(".wc-configurator__variant-card")
        .querySelector(".wc-configurator__variant-label");
      summaryVariant.textContent = varLabel ? varLabel.textContent : "";
    }

    // Summary options list (tiers + add-ons)
    if (summaryOptions) {
      summaryOptions.innerHTML = "";

      // Tier selections
      selectedTiers.forEach((radio) => {
        const detail = optionDetails[radio.value] || {};
        const catSection = radio.closest("[data-category]");
        const iconEl = catSection
          ? catSection.querySelector(".wc-configurator__section-icon")
          : null;
        const li = document.createElement("li");
        const iconHtml = iconEl ? iconEl.innerHTML : "";
        li.innerHTML =
          `<span class="wc-configurator__summary-option-icon">${iconHtml}</span>` +
          `<span class="wc-configurator__summary-option-label">${detail.title || radio.value}</span>` +
          `<span class="wc-configurator__summary-option-price">${formatPrice(Number(radio.dataset.price))}</span>`;
        summaryOptions.appendChild(li);
      });

      // Add-on selections
      selectedOpts.forEach((cb) => {
        const optEl = cb.closest(".wc-configurator__option");
        const optLabel = optEl.querySelector(".wc-configurator__option-label");
        const catSection = optEl.closest("[data-category]");
        const iconEl = catSection
          ? catSection.querySelector(".wc-configurator__section-icon")
          : null;
        const li = document.createElement("li");
        const iconHtml = iconEl ? iconEl.innerHTML : "";
        li.innerHTML =
          `<span class="wc-configurator__summary-option-icon">${iconHtml}</span>` +
          `<span class="wc-configurator__summary-option-label">${optLabel ? optLabel.textContent : cb.value}</span>` +
          `<span class="wc-configurator__summary-option-price">${formatPrice(Number(cb.dataset.price))}</span>`;
        summaryOptions.appendChild(li);
      });
    }

    // Total
    if (totalDisplay) {
      totalDisplay.textContent = formatPrice(total);
    }
    if (mobileTotalDisplay) {
      mobileTotalDisplay.textContent = formatPrice(total);
    }

    // Populate hidden form fields
    if (fieldVariant && variantInput) {
      const varLabel = variantInput
        .closest(".wc-configurator__variant-card")
        .querySelector(".wc-configurator__variant-label");
      fieldVariant.value = varLabel
        ? varLabel.textContent.trim()
        : variantInput.value;
    }

    if (fieldOptions) {
      const tierNames = selectedTiers.map((r) => {
        const detail = optionDetails[r.value] || {};
        const price = formatPrice(Number(r.dataset.price));
        return `${detail.title || r.value} (${price})`;
      });
      const addonNames = selectedOpts.map((cb) => {
        const optLabel = cb
          .closest(".wc-configurator__option")
          .querySelector(".wc-configurator__option-label");
        const price = formatPrice(Number(cb.dataset.price));
        return `${optLabel ? optLabel.textContent.trim() : cb.value} (${price})`;
      });
      fieldOptions.value = [...tierNames, ...addonNames].join("; ");
    }

    if (fieldTotal) {
      fieldTotal.value = formatPrice(total);
    }

    saveState();
  }

  // ── Info popup ──

  function openPopup(optionId, trigger) {
    if (!popup) {
      return;
    }
    const detail = optionDetails[optionId];
    if (!detail) {
      return;
    }

    // Single-option view: title + price + description, no comparison list.
    popupTitle.textContent = detail.title;
    popupDesc.textContent = detail.desc;
    popupDesc.hidden = false;
    popupPrice.textContent = formatPrice(detail.price);
    popupPrice.hidden = false;
    if (popupList) {
      popupList.hidden = true;
      popupList.innerHTML = "";
    }

    openDialog(popup, trigger);
  }

  // Per-group "compare details": one popup listing every tier of the group
  // (the Serie default + each upgrade) with its price and description.
  function openGroupPopup(groupKey, trigger) {
    if (!popup || !popupList) {
      return;
    }
    const group = root.querySelector(
      `.wc-configurator__tier-group[data-tier-group="${groupKey}"]`,
    );
    if (!group) {
      return;
    }
    const isEn = document.documentElement.lang === "en";

    const titleEl = group.querySelector(".wc-configurator__tier-group-title");
    popupTitle.textContent = titleEl
      ? titleEl.textContent.trim()
      : isEn
        ? "Compare details"
        : "Details vergleichen";

    const rows = [];
    // Serie default (first card, no option id)
    const defCard = group.querySelector(".wc-configurator__tier-card");
    if (defCard) {
      const name = defCard.querySelector(".wc-configurator__tier-headline");
      const sub = defCard.querySelector(".wc-configurator__tier-sub");
      rows.push(
        `<div class="wc-configurator__group-detail-row">` +
          `<span class="wc-configurator__group-detail-name">${name ? name.textContent.trim() : ""}</span>` +
          `<span class="wc-configurator__group-detail-price wc-configurator__group-detail-price--serie">${isEn ? "Included" : "Serie"}</span>` +
          `</div>` +
          (sub
            ? `<p class="wc-configurator__group-detail-desc">${sub.textContent.trim()}</p>`
            : ""),
      );
    }
    // Each upgrade tier
    group.querySelectorAll("[data-option-id]").forEach((card) => {
      const id = card.dataset.optionId;
      const d = optionDetails[id] || {};
      rows.push(
        `<div class="wc-configurator__group-detail-row">` +
          `<span class="wc-configurator__group-detail-name">${d.title || id}</span>` +
          `<span class="wc-configurator__group-detail-price">+${formatPrice(d.price || 0)}</span>` +
          `</div>` +
          (d.desc
            ? `<p class="wc-configurator__group-detail-desc">${d.desc}</p>`
            : ""),
      );
    });

    popupList.innerHTML = rows.join("");
    popupList.hidden = false;
    popupDesc.hidden = true;
    popupPrice.hidden = true;

    openDialog(popup, trigger);
  }

  function closePopup() {
    if (!popup) {
      return;
    }
    closeDialog(popup);
  }

  // ── Quote popup ──

  const quotePopup = root.querySelector("[data-quote-popup]");
  const quoteSummary = quotePopup
    ? quotePopup.querySelector("[data-quote-summary]")
    : null;

  function openQuote() {
    if (!quotePopup) {
      return;
    }
    update();
    fireBeginCheckout();

    // Build structured summary for the popup
    if (quoteSummary) {
      quoteSummary.innerHTML = "";

      const variantInput = getActiveVariant();
      if (variantInput) {
        const varLabel = variantInput
          .closest(".wc-configurator__variant-card")
          .querySelector(".wc-configurator__variant-label");
        const varRow = document.createElement("div");
        varRow.className = "wc-configurator__quote-summary-variant";
        varRow.innerHTML =
          `<span>${varLabel ? varLabel.textContent.trim() : ""}</span>` +
          `<span>${formatPrice(Number(variantInput.dataset.price))}</span>`;
        quoteSummary.appendChild(varRow);
      }

      const selectedTiers = getSelectedTiers();
      const selectedOpts = getSelectedOptions();
      if (selectedTiers.length || selectedOpts.length) {
        const list = document.createElement("ul");
        list.className = "wc-configurator__quote-summary-options";
        selectedTiers.forEach((radio) => {
          const detail = optionDetails[radio.value] || {};
          const catSection = radio.closest("[data-category]");
          const iconEl = catSection
            ? catSection.querySelector(".wc-configurator__section-icon")
            : null;
          const li = document.createElement("li");
          li.innerHTML =
            `<span class="wc-configurator__summary-option-icon">${iconEl ? iconEl.innerHTML : ""}</span>` +
            `<span class="wc-configurator__summary-option-label">${detail.title || radio.value}</span>` +
            `<span class="wc-configurator__summary-option-price">${formatPrice(Number(radio.dataset.price))}</span>`;
          list.appendChild(li);
        });
        selectedOpts.forEach((cb) => {
          const optEl = cb.closest(".wc-configurator__option");
          const optLabel = optEl.querySelector(".wc-configurator__option-label");
          const catSection = optEl.closest("[data-category]");
          const iconEl = catSection
            ? catSection.querySelector(".wc-configurator__section-icon")
            : null;
          const li = document.createElement("li");
          li.innerHTML =
            `<span class="wc-configurator__summary-option-icon">${iconEl ? iconEl.innerHTML : ""}</span>` +
            `<span class="wc-configurator__summary-option-label">${optLabel ? optLabel.textContent.trim() : cb.value}</span>` +
            `<span class="wc-configurator__summary-option-price">${formatPrice(Number(cb.dataset.price))}</span>`;
          list.appendChild(li);
        });
        quoteSummary.appendChild(list);
      }

      const totalEl = root.querySelector("[data-configurator-total]");
      if (totalEl) {
        const totalRow = document.createElement("div");
        totalRow.className = "wc-configurator__quote-summary-total";
        totalRow.innerHTML =
          `<span>${document.documentElement.lang === "en" ? "Total" : "Gesamt"}</span>` +
          `<strong>${totalEl.textContent}</strong>`;
        quoteSummary.appendChild(totalRow);
      }
    }

    openDialog(quotePopup);
  }

  function closeQuote() {
    if (!quotePopup) {
      return;
    }
    closeDialog(quotePopup);
  }

  // Click handlers
  root.addEventListener("click", (e) => {
    // Per-group "compare details" open
    const groupInfoBtn = e.target.closest("[data-group-info]");
    if (groupInfoBtn) {
      e.preventDefault();
      e.stopPropagation();
      openGroupPopup(groupInfoBtn.dataset.groupInfo, groupInfoBtn);
      return;
    }

    // Info popup open
    const infoBtn = e.target.closest("[data-option-info]");
    if (infoBtn) {
      e.preventDefault();
      e.stopPropagation();
      openPopup(infoBtn.dataset.optionInfo, infoBtn);
      return;
    }

    // Info popup close
    const closeInfoBtn = e.target.closest("[data-info-popup-close]");
    if (closeInfoBtn) {
      closePopup();
      return;
    }

    // Quote popup open
    const quoteOpenBtn = e.target.closest("[data-quote-open]");
    if (quoteOpenBtn) {
      lastFocusedTrigger = quoteOpenBtn;
      openQuote();
      return;
    }

    // Quote popup close
    if (e.target.closest("[data-quote-close]")) {
      closeQuote();
      return;
    }

    // Confirm accept
    if (e.target.closest("[data-confirm-accept]")) {
      acceptConfirm();
      return;
    }

    // Confirm cancel
    if (e.target.closest("[data-confirm-cancel]")) {
      cancelConfirm();
      return;
    }

    // Tier card click → select radio
    const tierCard = e.target.closest(".wc-configurator__tier-card");
    if (tierCard && !e.target.closest(".wc-configurator__info-btn")) {
      const radio = tierCard.querySelector("[data-configurator-tier]");
      if (radio && !radio.disabled) {
        radio.checked = true;
        update(radio);
      }
      return;
    }

    // Add-on card click → toggle checkbox (skip toggle and info button)
    const card = e.target.closest(".wc-configurator__option[data-option-id]");
    if (card && !e.target.closest(".wc-configurator__option-toggle") && !e.target.closest(".wc-configurator__info-btn")) {
      const cb = card.querySelector("[data-configurator-option]");
      if (cb && !cb.disabled) {
        cb.checked = !cb.checked;
        update(cb);
      }
      return;
    }

  });

  // Close any popup on Escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (popup && !popup.hidden) {
        closePopup();
      }
      if (quotePopup && !quotePopup.hidden) {
        closeQuote();
      }
      if (confirmPopup && !confirmPopup.hidden) {
        cancelConfirm();
      }
    }
  });

  // ── Change events ──
  root.addEventListener("change", (e) => {
    if (e.target.matches("[data-configurator-variant]")) {
      update();
    } else if (e.target.matches("[data-configurator-option]")) {
      update(e.target);
    } else if (e.target.matches("[data-configurator-tier]")) {
      update(e.target);
    }
  });

  // ── Form submit: update fields before sending ──
  const quoteForm = root.querySelector("[data-configurator-form]");
  if (quoteForm) {
    quoteForm.addEventListener("submit", () => {
      update();
    });
  }

  // ── Accordion: one category open at a time ──
  // Keeps the page from becoming a long wall of every category at once;
  // the sticky summary on the right stays the running overview of choices.
  const collapsibles = Array.from(root.querySelectorAll("details[data-category]"));
  collapsibles.forEach((details) => {
    details.addEventListener("toggle", () => {
      if (!details.open) {
        return;
      }
      collapsibles.forEach((other) => {
        if (other !== details) {
          other.open = false;
        }
      });
      // Categories above just collapsed, so this one jumps upward. Pull its
      // header back into view if the collapse pushed it off-screen.
      requestAnimationFrame(() => {
        const summary = details.querySelector("summary");
        if (!summary) {
          return;
        }
        const top = summary.getBoundingClientRect().top;
        if (top < 0 || top > window.innerHeight * 0.6) {
          summary.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    });
  });

  // Restore saved state, then run initial update
  restoreState();
  update();

  // GA4 — page-level engagement signal
  fireViewItem();

  // Hide the sticky mobile bar once the inline summary is in view, so total +
  // CTA aren't doubled up on small screens.
  const summary = root.querySelector("[data-configurator-summary]");
  const mobileBar = root.querySelector(".wc-configurator__mobile-bar");
  if (summary && mobileBar && "IntersectionObserver" in window) {
    const barObserver = new IntersectionObserver(
      ([entry]) => {
        mobileBar.classList.toggle(
          "wc-configurator__mobile-bar--hidden",
          entry.isIntersecting,
        );
      },
      { rootMargin: "0px 0px -40px 0px" },
    );
    barObserver.observe(summary);
  }

  // GA4 event helpers ---------------------------------------------------------
  function modelDisplayName() {
    return modelKey === "core" ? "VANYX Core" : "VANYX";
  }

  function currentVariantInfo() {
    const variant = getActiveVariant();
    if (!variant) {
      return { label: "", price: 0 };
    }
    const card = variant.closest(".wc-configurator__variant-card");
    const labelEl = card ? card.querySelector(".wc-configurator__variant-label") : null;
    return {
      label: labelEl ? labelEl.textContent.trim() : "",
      price: Number(variant.dataset.price) || 0,
    };
  }

  function fireViewItem() {
    if (typeof window.gtag !== "function") {
      return;
    }
    const variant = currentVariantInfo();
    window.gtag("event", "view_item", {
      currency: "EUR",
      value: variant.price,
      items: [
        {
          item_id: modelKey,
          item_name: modelDisplayName(),
          item_brand: "VANYX",
          item_category: "Camper Van",
          item_variant: variant.label,
          price: variant.price,
        },
      ],
    });
  }

  function fireBeginCheckout() {
    if (typeof window.gtag !== "function") {
      return;
    }
    const totalEl = root.querySelector("[data-configurator-total]");
    const total = totalEl ? Number(totalEl.textContent.replace(/[^\d]/g, "")) || 0 : 0;
    const variant = currentVariantInfo();
    const items = [
      {
        item_id: modelKey,
        item_name: modelDisplayName(),
        item_brand: "VANYX",
        item_category: "Camper Van",
        item_variant: variant.label,
        price: variant.price,
      },
      ...getSelectedTiers().map((r) => ({
        item_id: r.value,
        item_name: (optionDetails[r.value] || {}).title || r.value,
        item_brand: "VANYX",
        item_category: "Option",
        price: Number(r.dataset.price) || 0,
      })),
      ...getSelectedOptions().map((cb) => ({
        item_id: cb.value,
        item_name: (optionDetails[cb.value] || {}).title || cb.value,
        item_brand: "VANYX",
        item_category: "Option",
        price: Number(cb.dataset.price) || 0,
      })),
    ];
    window.gtag("event", "begin_checkout", {
      currency: "EUR",
      value: total,
      items,
    });
  }

}
